/**
 * Objets d'un bureau personnel : à qui ils sont (sous-chemin
 * `@campus/core/desk-access`, sans dépendance, utilisable par le temps réel).
 *
 * RÈGLE : tout ce qui est posé dans un bureau personnel reste personnel. Un
 * visiteur peut entrer dans le bureau (c'est une bulle) et parler, mais ne
 * touche à rien : s'asseoir, ouvrir un tableau blanc, et toute interaction
 * à venir sur un objet de bureau passent par `canUseDeskItem`, côté jeu (on ne
 * propose pas l'action) ET côté serveur (on la refuse).
 *
 * Partage : plus tard, le propriétaire pourra autoriser d'autres membres
 * (`sharedWith`) ; ce sera toujours son accord explicite, jamais un défaut.
 */

export interface DeskOwnership {
  ownerUserId: string;
  /** Membres autorisés par le propriétaire (partage à venir). */
  sharedWith?: ReadonlyArray<string>;
}

/** Bureau personnel situé sur la carte, en px. */
export interface DeskBounds extends DeskOwnership {
  bounds: { x: number; y: number; width: number; height: number };
}

/** Vrai si `userId` peut utiliser les objets de ce bureau : son propriétaire, ou quelqu'un qu'il a autorisé. */
export function canUseDeskItem(desk: DeskOwnership, userId: string): boolean {
  return desk.ownerUserId === userId || !!desk.sharedWith?.includes(userId);
}

/** Bureau qui contient le point (px), ou null. Bords inclus. */
export function deskAtPoint<T extends DeskBounds>(desks: Iterable<T>, x: number, y: number): T | null {
  for (const desk of desks) {
    const b = desk.bounds;
    if (x >= b.x && x <= b.x + b.width && y >= b.y && y <= b.y + b.height) return desk;
  }
  return null;
}

/** Vrai si l'objet (ou l'action) placé en (x, y) est utilisable par `userId` : hors de tout bureau, ou dans un bureau qu'il peut utiliser. */
export function canUseAt(desks: Iterable<DeskBounds>, userId: string, x: number, y: number): boolean {
  const desk = deskAtPoint(desks, x, y);
  return !desk || canUseDeskItem(desk, userId);
}
